// 生产环境后端 — 临时备用站走 api.kommends.com (Render free tier)
// 原 GCP Cloud Run 因 2026-05-10 凭证泄漏 incident 暂时 suspend，
// GCP 恢复后可改回 'https://socialy-api-821240505887.asia-southeast1.run.app'。
// 跨子域 (kommends.com → api.kommends.com) 是 same-site，cookie SameSite=Lax 工作。
const PROD_API_URL = 'https://api.kommends.com'

function getEnv() {
  return (import.meta as unknown as { env?: { [k: string]: string | undefined } }).env || {}
}

/**
 * 移动端（Capacitor）推荐配置一个“网关域名”，避免直连单一区域后端导致变慢：
 * - VITE_CAPACITOR_API_ORIGIN=https://ynexus.co        （走 Firebase Hosting rewrites）
 * - VITE_CAPACITOR_API_ORIGIN=https://api.ynexus.co    （走 Cloudflare Workers 网关）
 *
 * 未配置时，回退到 PROD_API_URL（可能跨区慢，但保证可用）。
 */
function getCapacitorApiOrigin(): string {
  const env = getEnv()
  const v =
    env.VITE_CAPACITOR_API_ORIGIN ||
    env.VITE_APP_API_ORIGIN ||
    env.VITE_API_ORIGIN ||
    ''
  return String(v || '').trim()
}

/**
 * 检测是否在 Capacitor 原生环境中运行
 * 注意：Capacitor 可能使用 https://localhost，所以不能单纯靠 hostname 判断
 */
export function isCapacitorEnv(): boolean {
  if (typeof window === 'undefined') return false
  const protocol = window.location.protocol
  // 检测 Capacitor 对象（最可靠的方式）
  const cap = (window as unknown as { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor
  if (cap) {
    // 如果有 isNativePlatform 方法，用它判断
    if (typeof cap.isNativePlatform === 'function') {
      return cap.isNativePlatform()
    }
    // 否则只要 Capacitor 对象存在就认为是原生环境
    return true
  }
  // 兜底：检测特殊协议
  return protocol === 'capacitor:' || protocol === 'file:' || protocol === 'ionic:'
}

/**
 * 检测是否是真正的本地开发环境（浏览器中，非 Capacitor）
 */
export function isLocalDevEnv(): boolean {
  if (typeof window === 'undefined') return false
  // 先排除 Capacitor 环境
  if (isCapacitorEnv()) return false
  const host = window.location.hostname || ''
  return host === 'localhost' || host === '127.0.0.1'
}

export function apiBase(): string {
  // ⚠️ 临时开发模式：Android 模拟器访问本地后端
  // 10.0.2.2 是 Android 模拟器访问宿主机的特殊 IP
  // 设置为 true 时使用本地后端，false 时使用生产后端
  const USE_LOCAL_BACKEND_FOR_EMULATOR = false
  const EMULATOR_LOCAL_URL = 'http://10.0.2.2:8084'
  
  if (typeof window !== 'undefined') {
    // Capacitor 移动端
    if (isCapacitorEnv()) {
      // 临时开发模式：使用本地后端
      if (USE_LOCAL_BACKEND_FOR_EMULATOR) return EMULATOR_LOCAL_URL
      // 生产模式：优先使用“网关域名”，否则兜底直连后端
      const gateway = getCapacitorApiOrigin()
      return gateway || PROD_API_URL
    }
    
    // 本地开发环境使用代理（空字符串 = 相对路径）
    if (isLocalDevEnv()) return ''

    // 生产 Web — 直接调 PROD_API_URL (api.kommends.com)。
    // 不读 envBase 因为 .env 里 VITE_API_BASE_URL=http://localhost:8084 是 dev 用的，
    // 会被 vite build 打进 production bundle 污染线上。
    return PROD_API_URL
  }
  return PROD_API_URL
}

export function apiUrl(path: string): string {
  const p = path || ''
  
  // Capacitor 移动端：使用生产后端
  if (isCapacitorEnv()) {
    return `${apiBase()}${p}`
  }
  
  // 本地开发环境：使用相对路径（通过 Vite proxy）
  if (isLocalDevEnv()) {
    return p
  }
  
  // 生产 Web 或 SSR：拼绝对路径（备用站没有同源 rewrite）
  return `${apiBase()}${p}`
}

// Account status detail strings returned by backend dependencies.py
const ACCOUNT_SUSPENDED_DETAIL = 'account_suspended'
const ACCOUNT_DELETED_DETAIL = 'account_deleted'

/**
 * Detect language from current URL path or fallback to stored preference.
 * Used outside React (no hooks) — reads Zustand store directly.
 */
function getCurrentLanguage(): string {
  // Try URL first: /en/... or /zh-CN/... or /ja/...
  if (typeof window !== 'undefined') {
    const match = window.location.pathname.match(/^\/(en|zh-CN|ja)(?:\/|$)/)
    if (match) return match[1]
  }
  // Fallback: Zustand persisted value in localStorage
  try {
    const raw = localStorage.getItem('language-storage')
    if (raw) {
      const parsed = JSON.parse(raw)
      return parsed?.state?.language || 'en'
    }
  } catch { /* ignore */ }
  return 'en'
}

/**
 * Handle 403 responses for suspended/deleted accounts.
 * Redirects to /suspended or to home for deleted accounts.
 */
function handleAccountBlock(response: Response): void {
  if (response.status !== 403 || typeof window === 'undefined') return

  // Clone to avoid consuming the body for callers that don't hit this path
  response.clone().json().then((body) => {
    const detail = body?.detail
    if (detail === ACCOUNT_SUSPENDED_DETAIL) {
      const lang = getCurrentLanguage()
      window.location.href = `/${lang}/suspended`
    } else if (detail === ACCOUNT_DELETED_DETAIL) {
      // Cookie session: server-side session is already invalid by the time we
      // see this 403, so just redirect home — the next request will 401 and
      // AuthContext will clear React state.
      window.location.href = '/'
    }
  }).catch(() => {
    // Non-JSON 403 — not an account block, let caller handle
  })
}

/**
 * Cookie-session aware fetch — sends the __session cookie automatically.
 * No Bearer header injection (server reads request.session via starsessions).
 *
 * - 403 account_suspended → redirect /suspended
 * - 403 account_deleted → redirect /
 * - 401 is NOT auto-retried: cookie session has no client-side refresh.
 *   Caller (or React Query) decides whether to surface the error or trigger
 *   re-login via AuthContext.
 */
export async function fetchWithAuth(
  url: string,
  options: RequestInit = {}
): Promise<Response> {
  const response = await fetch(url, {
    ...options,
    credentials: 'include',
  })

  if (response.status === 403) {
    handleAccountBlock(response)
  }

  return response
}
